import { TableColumn } from "react-data-table-component";
import { sortByDate, transformDateUTC } from "utils/date-utils";
import { StatusChip } from "./components/StatusChip";
import { DataRow } from "./types";
import { TableCell } from "../UserInfo/components/TreatmentHistoryTable/styles";

const byStartDateThenEndDate = (rowA: DataRow, rowB: DataRow): number => {
  const startA = rowA.startOn;
  const startB = rowB.startOn;
  
  // First sort by start date (descending - newer dates first)
  if (startA && startB) {
    const dateComparison = sortByDate(startB, startA, false);
    if (dateComparison !== 0) {
      return dateComparison;
    }
  }
  
  // If dates are the same or missing, then sort by program type (intensive before maintenance)
  const isIntensiveA = rowA.playlistName.toLowerCase().includes("intensive");
  const isIntensiveB = rowB.playlistName.toLowerCase().includes("intensive");
  
  if (isIntensiveA !== isIntensiveB) {
    return isIntensiveA ? -1 : 1;
  }
  
  // Extract round numbers from playlist names
  const roundA = parseInt(rowA.playlistName.match(/Round (\d+)/i)?.[1] || "0");
  const roundB = parseInt(rowB.playlistName.match(/Round (\d+)/i)?.[1] || "0");
  
  // Then sort by round number (descending - higher rounds first)
  if (roundA !== roundB) {
    return roundB - roundA;
  }
  
  // Extract week numbers from playlist names
  const weekA = parseInt(rowA.playlistName.match(/week (\d+)/i)?.[1] || "0");
  const weekB = parseInt(rowB.playlistName.match(/week (\d+)/i)?.[1] || "0");
  
  // Within the same round, sort by week number (descending - higher weeks first)
  if (weekA !== weekB) {
    return weekB - weekA;
  }
  
  // Final fallback: sort by end date
  if (startA && startB && startA === startB) {
    return sortByDate(rowB.endOn, rowA.endOn, false);
  }
  
  return 0;
};

const byStatusThenStartDateThenEndDate = (
  rowA: DataRow,
  rowB: DataRow
): number => {
  const statusA = rowA.isActive;
  const statusB = rowB.isActive;

  if (statusA === statusB) {
    return byStartDateThenEndDate(rowA, rowB);
  }

  if (!statusA > !statusB) {
    return -1;
  }
  if (!statusA < !statusB) {
    return 1;
  }
  return 0;
};

export const columns: TableColumn<DataRow>[] = [
  {
    name: "ID",
    selector: (row) => row.id,
    omit: true,
  },
  {
    name: "Playlist Name",
    selector: (row) => row.playlistName,
    cell: (row) =>
      row.playlistName ? <TableCell>{row.playlistName}</TableCell> : "",
    sortable: true,
  },
  {
    name: "Start & End Date",
    selector: (row) => {
      return !row.startOn && !row.endOn
        ? "-"
        : `${transformDateUTC(row.startOn?.toString())} - ${transformDateUTC(
            row.endOn?.toString()
          )}`;
    },
    sortable: true,
    sortFunction: byStartDateThenEndDate,
  },
  {
    name: "# of Activities",
    selector: (row) => row.numOfActivities,
    sortable: true,
  },
  {
    name: "Status",
    cell: (row) => <StatusChip isActive={row.isActive} />,
    sortable: true,
    sortFunction: byStatusThenStartDateThenEndDate,
  },
];
